// src/components/AdminVideoList.js
import React, { useEffect, useState } from 'react';
import { db, storage } from '../firebase';
import { collection, query, orderBy, limit, startAfter, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { ref as storageRef, deleteObject } from 'firebase/storage';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';

function AdminVideoList() {
  const [videos, setVideos] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);

  const VIDEOS_PER_PAGE = 10;

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const videosQuery = query(collection(db, 'videos'), orderBy('createdAt', 'desc'), limit(VIDEOS_PER_PAGE));
      const videosSnapshot = await getDocs(videosQuery);

      setVideos(videosSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLastDoc(videosSnapshot.docs[videosSnapshot.docs.length - 1]);
      setHasMore(videosSnapshot.docs.length === VIDEOS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to fetch videos.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreVideos = async () => {
    if (!lastDoc) return;

    try {
      const videosQuery = query(collection(db, 'videos'), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(VIDEOS_PER_PAGE));
      const videosSnapshot = await getDocs(videosQuery);

      setVideos((prev) => [...prev, ...videosSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))]);
      setLastDoc(videosSnapshot.docs[videosSnapshot.docs.length - 1]);
      setHasMore(videosSnapshot.docs.length === VIDEOS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching more videos:', error);
      toast.error('Failed to load more videos.');
    }
  };

  const handleDeleteVideo = async (videoId, videoUrl, thumbnailUrl) => {
    try {
      await deleteDoc(doc(db, 'videos', videoId));

      if (videoUrl) {
        await deleteObject(storageRef(storage, videoUrl));
      }
      if (thumbnailUrl) {
        await deleteObject(storageRef(storage, thumbnailUrl));
      }

      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      toast.success('Video deleted successfully.');
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6">Videos</h2>

      {loading ? (
        <div className="flex justify-center">
          <ClipLoader size={50} color="#123abc" />
        </div>
      ) : (
        <InfiniteScroll dataLength={videos.length} next={fetchMoreVideos} hasMore={hasMore} loader={<ClipLoader size={30} color="#123abc" />} endMessage={<p className="text-center text-gray-500">No more videos to display.</p>}>
          <table className="w-full border-collapse border border-gray-200 bg-white shadow rounded">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">Title</th>
                <th className="border px-4 py-2">User ID</th>
                <th className="border px-4 py-2">Created At</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {videos.map((video) => (
                <tr key={video.id} className="hover:bg-gray-100">
                  <td className="border px-4 py-2">{video.title || 'Untitled'}</td>
                  <td className="border px-4 py-2">{video.userId || 'N/A'}</td>
                  <td className="border px-4 py-2">{video.createdAt?.toDate().toLocaleString() || 'N/A'}</td>
                  <td className="border px-4 py-2">
                    <button onClick={() => handleDeleteVideo(video.id, video.videoUrl, video.thumbnailUrl)} className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
      )}
    </div>
  );
}

export default AdminVideoList;
